import * as authService from "../services/authService";
import { styled } from "baseui";
import { Button } from "baseui/button";
import { Input } from "baseui/input";
import { toaster } from "baseui/toast";
import { HeadingMedium, LabelSmall, ParagraphSmall } from "baseui/typography";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { useS3Image } from "../hooks/useS3Image";

const Container = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  backgroundColor: "#ffffff",
  gap: "24px",
  paddingBottom: "10vh",
});

const CardContainer = styled("div", {
  width: "350px",
  padding: "20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const ErrorMessage = styled(ParagraphSmall, {
  color: "#e54937",
  margin: "0",
});

const LogoContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "42px",
  // SAM-1309 (U27): the logo arrives after the page does; its height is reserved so the form does not jump.
  height: "160px",
});

type Props = {
  onSignIn?: () => void;
  errorMessage?: string | null;
};

function LoginPage({ onSignIn, errorMessage }: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // Cognito returns CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED for users in
  // FORCE_CHANGE_PASSWORD status (created by an admin with a temporary
  // password). When that happens we flip the form to collect a permanent
  // password and finish auth via confirmSignIn.
  const [mode, setMode] = useState<'login' | 'newPassword' | 'resetPassword'>('login');
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  // Kept across the NEW_PASSWORD_REQUIRED round-trip (backend needs the original
  // password + session to complete the challenge).
  const [savedPassword, setSavedPassword] = useState("");
  const [session, setSession] = useState<string | null>(null);
  const { tenantId, tenantLogo, tenantName, tenantPrimaryColor } = useTenantBranding();
  const { imageUrl: logoUrl } = useS3Image(tenantLogo);

  useEffect(() => {
    if (errorMessage) setLoginError(errorMessage);
  }, [errorMessage]);

  const handleLoginKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && password && !isLoading) handleLogin();
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setLoginError(null);
    try {
      // Send the community resolved from the domain. Without it the backend
      // has to guess the pool from the email alone, and it answers with the
      // first pool in the tenant map that holds that address — so an operator
      // who exists in more than one community always lands in the first one.
      const result = await authService.login(email, password, tenantId || null);
      if (result.result === 'NEW_PASSWORD_REQUIRED') {
        // Backend needs the original password + session to complete the challenge.
        setSavedPassword(password);
        setSession(result.session);
        setMode('newPassword');
        setPassword("");
        return;
      }
      if (result.result === 'SUCCESS') {
        onSignIn?.();
        return;
      }
      setLoginError(result.message);
      toaster.negative(result.message, { autoHideDuration: 3000 });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setLoginError(errorMsg);
      toaster.negative(errorMsg, { autoHideDuration: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoginError(null);
    if (!email) {
      setLoginError("Enter your email first.");
      return;
    }
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      toaster.positive("A verification code was sent to your email.", { autoHideDuration: 4000 });
      setMode('resetPassword');
      setPassword("");
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to start password reset";
      setLoginError(errorMsg);
      toaster.negative(errorMsg, { autoHideDuration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async () => {
    setLoginError(null);
    if (newPassword.length < 8) {
      setLoginError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLoginError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      const result = await authService.confirmNewPassword(email, savedPassword, newPassword, session || "");
      if (result.result === 'SUCCESS') {
        toaster.positive("Password updated. You're now signed in.", { autoHideDuration: 3000 });
        onSignIn?.();
        return;
      }
      const message = result.result === 'ERROR' ? result.message : 'Failed to set new password';
      setLoginError(message);
      toaster.negative(message, { autoHideDuration: 4000 });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to set new password";
      setLoginError(errorMsg);
      toaster.negative(errorMsg, { autoHideDuration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    setLoginError(null);
    if (!verificationCode) {
      setLoginError("Please enter the verification code sent to your email.");
      return;
    }
    if (newPassword.length < 8) {
      setLoginError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLoginError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await authService.confirmResetPassword(email, verificationCode, newPassword);
      toaster.positive("Password reset successfully. Please log in with your new password.", { autoHideDuration: 4000 });
      setMode('login');
      setNewPassword("");
      setConfirmPassword("");
      setVerificationCode("");
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to reset password";
      setLoginError(errorMsg);
      toaster.negative(errorMsg, { autoHideDuration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelNewPassword = () => {
    setMode('login');
    setNewPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setSession(null);
    setSavedPassword("");
    setLoginError(null);
  };

  return (
    <Container>
      <LogoContainer>
        {logoUrl ? (
          <img src={logoUrl} alt={tenantName} style={{ maxHeight: '160px', maxWidth: '360px', objectFit: 'contain' }} />
        ) : tenantName && !tenantLogo ? (
          // The name stands in only for a tenant with no logo; while a logo is loading the space stays empty.
          <div style={{ fontSize: '22px', fontWeight: 600, color: tenantPrimaryColor }}>
            {tenantName}
          </div>
        ) : null}
      </LogoContainer>

      <CardContainer>
        {mode === 'login' ? (
          <>
            <HeadingMedium marginBottom={0}>Log in</HeadingMedium>

            <LabelSmall>Email or Employee code</LabelSmall>
            <Input
              value={email}
              onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="Email or Employee code"
              type="text"
              onKeyDown={handleLoginKeyDown}
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "16px" } } }}
            />
            <LabelSmall>Password</LabelSmall>
            <Input
              value={password}
              onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
              placeholder="Password"
              type="password"
              onKeyDown={handleLoginKeyDown}
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "16px" } } }}
            />

            {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

            <Button
              onClick={handleLogin}
              overrides={{
                BaseButton: {
                  style: {
                    width: "100%",
                    backgroundColor: tenantPrimaryColor,
                    ':hover': { backgroundColor: tenantPrimaryColor, opacity: 0.85 },
                  },
                },
              }}
              isLoading={isLoading}
              disabled={!email || !password || isLoading}
            >
              Log in
            </Button>
            <Button
              onClick={handleForgotPassword}
              kind="tertiary"
              disabled={isLoading}
              overrides={{ BaseButton: { style: { width: "100%" } } }}
            >
              Forgot password?
            </Button>
            {/* SAM-1298 (canvas W1): a business owner creates the account and sends the request. */}
            <div style={{ height: 1, background: "#eee" }} />
            <Button
              onClick={() => navigate("/register")}
              kind="tertiary"
              disabled={isLoading}
              overrides={{ BaseButton: { style: { width: "100%" } } }}
            >
              Register your business
            </Button>
          </>
        ) : mode === 'resetPassword' ? (
          <>
            <HeadingMedium marginBottom={0}>Reset your password</HeadingMedium>
            <ParagraphSmall style={{ margin: 0, color: '#6B7280' }}>
              A verification code was sent to your email. Enter it below along with your new password.
            </ParagraphSmall>

            <LabelSmall>Verification code</LabelSmall>
            <Input
              value={verificationCode}
              onChange={(e) => setVerificationCode((e.target as HTMLInputElement).value)}
              placeholder="Enter verification code"
              type="text"
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "8px" } } }}
            />
            <LabelSmall>New password</LabelSmall>
            <Input
              value={newPassword}
              onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
              placeholder="New password"
              type="password"
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "8px" } } }}
            />
            <LabelSmall>Confirm new password</LabelSmall>
            <Input
              value={confirmPassword}
              onChange={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
              placeholder="Confirm new password"
              type="password"
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "16px" } } }}
            />

            {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

            <Button
              onClick={handleConfirmResetPassword}
              overrides={{
                BaseButton: {
                  style: {
                    width: "100%",
                    backgroundColor: tenantPrimaryColor,
                    ':hover': { backgroundColor: tenantPrimaryColor, opacity: 0.85 },
                  },
                },
              }}
              isLoading={isLoading}
              disabled={!verificationCode || !newPassword || !confirmPassword || isLoading}
            >
              Reset password and continue
            </Button>
            <Button
              onClick={handleCancelNewPassword}
              kind="tertiary"
              disabled={isLoading}
              overrides={{ BaseButton: { style: { width: "100%" } } }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <HeadingMedium marginBottom={0}>Set a new password</HeadingMedium>
            <ParagraphSmall style={{ margin: 0, color: '#6B7280' }}>
              Your account requires a permanent password before you can continue.
            </ParagraphSmall>

            <LabelSmall>New password</LabelSmall>
            <Input
              value={newPassword}
              onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
              placeholder="New password"
              type="password"
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "8px" } } }}
            />
            <LabelSmall>Confirm new password</LabelSmall>
            <Input
              value={confirmPassword}
              onChange={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
              placeholder="Confirm new password"
              type="password"
              clearOnEscape
              overrides={{ Root: { style: { marginBottom: "16px" } } }}
            />

            {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

            <Button
              onClick={handleSetNewPassword}
              overrides={{
                BaseButton: {
                  style: {
                    width: "100%",
                    backgroundColor: tenantPrimaryColor,
                    ':hover': { backgroundColor: tenantPrimaryColor, opacity: 0.85 },
                  },
                },
              }}
              isLoading={isLoading}
              disabled={!newPassword || !confirmPassword || isLoading}
            >
              Set password and continue
            </Button>
            <Button
              onClick={handleCancelNewPassword}
              kind="tertiary"
              disabled={isLoading}
              overrides={{ BaseButton: { style: { width: "100%" } } }}
            >
              Cancel
            </Button>
          </>
        )}
      </CardContainer>
    </Container>
  );
}

export default LoginPage;
